// dashboard/constants.js
// Foundational constants shared across nearly every dashboard component —
// extracted first (2026-08-12 App.js split) because `c` (brand colors)
// alone is referenced 500+ times throughout the original App.js. Import
// this before anything else when splitting a new piece out.

import i18n from '../i18n';

export const FUNCTIONS_URL = 'https://gfjznrkxjbskijwurisd.supabase.co/functions/v1';

// ── Brand colors ──────────────────────────────────────────────────────────────
export const c = {
  fog:         '#F4F5F2',
  slate:       '#4F6473',
  slateDeep:   '#3a4d59',
  sandstone:   '#C2A98A',
  moss:        '#7A8F6B',
  textSoft:    '#7a8f9e',
  white:       '#ffffff',
  error:       '#9B4444',
  // Distinct from both c.error (form/validation) and c.red (routine RED
  // severity) — the highest-acuity tier, immediate_review, was previously
  // silently merged into c.red with no visual distinction at all.
  immediateReview:     '#5C2323',
  immediateReviewLight: '#f5e6e3',
  red:         '#8B3A3A',
  redLight:    '#fdf0f0',
  yellow:      '#C49A3C',
  yellowLight: '#fdf8ee',
  // c.yellow fails WCAG AA as bare text on white/fog (~2.6:1) — fine as a
  // border/pill-background accent, not as standalone text. Use this darker
  // same-family shade wherever yellow renders as plain text.
  yellowText:  '#7A5A1E',
  // Formalizes what were three ad-hoc hardcoded c.sandstoneLight/c.sandstoneText billing/
  // seat-limit/support-ticket "warning" colors scattered across this file
  // into one named, reused sandstone-family pair — also fixes c.sandstone
  // failing WCAG AA as bare text (~2.3:1). sandstoneLight recovered from the
  // original hardcoded '#FDF3EA'; sandstoneText is a darkened same-hue shade
  // (~5:1 against white/fog/sandstoneLight), same approach as yellow/yellowText.
  sandstoneLight: '#FDF3EA',
  sandstoneText:  '#7d6340',
  green:       '#7A8F6B',
  greenText:   '#4D6842',
  greenLight:  '#eef2eb',
  border:      '#e0e2de',
  rowHover:    '#f7f8f6',
};

// ── Cairn glyph — five stones, single source for every rendering of it
// (sidebar, login, SetPasswordScreen, print-summary header/PDF export)
// instead of the same path data hardcoded five separate times ─────────
export const CAIRN_GLYPH_PATHS = [
  'M 21 97 C 36 103, 68 103, 93 100 C 102 98, 107 93, 106 88 C 104 84, 85 84, 64 84 C 46 84, 27 86, 22 90 C 19 93, 19 96, 21 97 Z',
  'M 34 79 C 46 82, 70 82, 88 80 C 97 77, 101 73, 100 68 C 97 64, 80 63, 63 64 C 48 64, 36 65, 33 70 C 30 73, 31 77, 34 79 Z',
  'M 45 60 C 54 62, 67 62, 80 59 C 87 57, 90 52, 87 48 C 84 46, 72 46, 62 46 C 52 46, 43 47, 40 53 C 38 56, 40 58, 45 60 Z',
  'M 53 43 C 60 44, 69 44, 78 42 C 82 40, 84 37, 81 34 C 78 32, 70 32, 64 32 C 58 32, 51 33, 49 37 C 47 40, 49 42, 53 43 Z',
  'M 52 28 C 57 30, 65 30, 70 28 C 73 26, 73 22, 70 20 C 67 18, 61 18, 56 20 C 52 22, 50 25, 52 28 Z',
];

// ── Severity / condition-type helpers ───────────────────────────────────────
// Plain functions called from deep inside render trees all over the
// dashboard, not components — no useTranslation() hook access — so they
// call the i18next instance directly.
// 'muted' — a clinic-muted/inactive relationship (migration 183) is
// deliberately NOT rendered as green ("stable") — that would misreport a
// patient nobody is actively watching as clinically fine. Neutral gray
// instead, distinct from every real severity tier.
export const severityColor  = s => s === 'immediate_review' ? c.immediateReview : s === 'red' ? c.red    : s === 'yellow' ? c.yellow    : s === 'muted' ? c.textSoft : c.green;
export const severityBg     = s => s === 'immediate_review' ? c.immediateReviewLight : s === 'red' ? c.redLight : s === 'yellow' ? c.yellowLight : s === 'muted' ? c.fog : c.greenLight;
export const severityLabel  = s => i18n.t(`alertSeverity.${s === 'immediate_review' || s === 'red' || s === 'yellow' ? s : 'green'}`);

// Keys must match conditions.condition_type exactly (seeded in migration 03,
// srr added in migration 24) — 'oa' never matched the real seeded value
// 'osteoarthritis', so real OA patients silently fell through to the
// generic fallback, and 'srr' was missing entirely, rendering literally as
// "Srr" on this medically-precise surface.
export const CONDITION_TYPE_KEYS = new Set([
  'osteoarthritis', 'ckd', 'chf', 'srr', 'pruritus', 'qol_general', 'diabetes_mellitus', 'epilepsy',
  // Internal Medicine workflow expansion (2026-07-22, migration 96).
  'chronic_enteropathy', 'cognitive_dysfunction', 'hyperthyroidism', 'cushings_disease',
  'chronic_respiratory', 'oncology_support', 'addisons_disease', 'chronic_hepatopathy',
  'urinary_tract_disease', 'immune_mediated_disease',
  // Migration 77 — temporary compressed-cadence alpha-testing workflows. Delete before public beta.
  'alpha_osteoarthritis', 'alpha_ckd', 'alpha_qol_general', 'alpha_pruritus', 'alpha_diabetes_mellitus', 'alpha_epilepsy',
]);
export const conditionLabel = type => CONDITION_TYPE_KEYS.has(type)
  ? i18n.t(`conditionType.${type}`)
  : type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '—';

// Shared keyboard-activation handler for clickable rows/cards that use a div
// or tr instead of a real button — Enter/Space triggers the same callback a
// click would, matching native button semantics for keyboard/screen-reader users.
export const onActivateKeyDown = callback => e => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    callback();
  }
};

// ── Sidebar nav ───────────────────────────────────────────────────────────────
// labelKey resolved at render time (t(item.labelKey) in Sidebar, or
// navLabel in App.js) rather than here — this array is built once at
// module load, before i18n necessarily has the active language loaded,
// and it's outside any component so there's no hook access anyway.
export const NAV_ITEMS = [
  { id: 'dashboard',   labelKey: 'nav.dashboard',    icon: '⊞' },
  { id: 'enrollments', labelKey: 'nav.enrollments',  icon: '◎' },
  { id: 'workflows',   labelKey: 'nav.workflows',    icon: '⋯' },
  { id: 'permissions', labelKey: 'nav.permissions',  icon: '◈' },
  { id: 'billing',     labelKey: 'nav.billing',      icon: '◉' },
  { id: 'settings',    labelKey: 'nav.settings',     icon: '⚙' },
  { id: 'support',     labelKey: 'nav.support',      icon: '◷', managerOnly: true },
];

// Shared between buildEventsWidget (print summary), PatientDetailPanel's
// recent-events list, and PatientSection's card preview — one label map
// so all three surfaces can't drift apart for the same event.
export const EVENT_TYPE_KEYS = new Set([
  'unexpected_vet_visit', 'hospitalization', 'er_visit', 'surgery', 'other', 'missed_checkin',
  'wellness_visit', 'new_diagnosis', 'medication_started', 'medication_stopped', 'dose_changed', 'diet_changed', 'injury', 'boarding_travel',
]);

export const CHANGE_DOMAIN_KEYS = new Set([
  'energy_play', 'social_behavior', 'eating', 'drinking', 'urination', 'stool',
  'mobility_comfort', 'vomiting', 'breathing_coughing', 'skin_ears', 'weight_body_shape', 'other',
]);

// survey_evaluations.primary_reason_code, humanized for a clinic audience
// (concise, medically precise, per CLAUDE.md's clinic-facing tone — more
// technical than the owner-facing plain-language mapping in cairn-mobile's
// ConditionDetailScreen, deliberately not the same copy for the same
// codes). Used by TrendChart's per-dot hover tooltip (via
// formatEvaluationTooltip in utils.js).
export const REASON_CODE_CLINIC_LABEL = {
  SAFETY_OVERRIDE: 'Safety override — flagged response',
  ABSOLUTE_SEVERITY: 'Absolute severity',
  COMPOSITE_DRIFT: 'Composite drift from baseline',
  PERSISTENT_DRIFT: 'Persistent worsening trend',
  PERSISTENT_LOW_CONTROL: 'Persistent low control',
  SENTINEL_ABSOLUTE: 'Sentinel finding',
  NUMERIC_TREND: 'Numeric threshold',
  OWNER_REPORTED_WORSE: 'Owner-reported change',
  MULTI_FINDING_CLUSTER: 'Multi-finding cluster',
  SENTINEL_BASELINE_DELTA: 'Baseline delta',
  MISSED_MEDICATION: 'Missed medication',
  INSUFFICIENT_HISTORY: 'Insufficient history',
};
